import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { Bell, X } from 'lucide-react';
import { useHistory } from 'react-router';
import { toast } from 'sonner';
import { makeRESTApiRequest } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';
import { connectNotificationStream } from '../../helpers/notificationStream';
import AlertModal from '../AlertModal';
import moment from 'moment';

const NOTIFICATION_LABELS = {
  TASK_ASSIGNED: 'Task assigned',
  TASK_OVERDUE: 'Task overdue',
  TASK_COMPLETED: 'Task completed',
  CLOSING_DATE_CHANGED: 'Closing date updated',
  ORDER_ASSIGNED: 'Order assigned',
  BILLING_NOTE_RECEIVED: 'Billing note',
  CLIENT_ORDER_COMMENT: 'New comment'
};

// Types that need message formatting beyond the server-provided `message`.
const NOTIFICATION_MESSAGE_FORMATTERS = {
  TASK_OVERDUE: notification => {
    const due = notification.metadata?.dueDate;
    if (!due) return notification.message;

    const elapsedDays = moment()
      .startOf('day')
      .diff(moment(due).startOf('day'), 'days');

    return `${notification.message} ${elapsedDays} biz days elapsed`;
  }
};

const getNotificationMessage = notification => {
  const format = NOTIFICATION_MESSAGE_FORMATTERS[notification.type];
  return format ? format(notification) : notification.message;
};

const getServiceNotificationPath = notification => {
  const serviceId = notification?.metadata?.service;
  if (!serviceId) return null;

  const orderType = notification?.metadata?.orderType;
  if (!orderType) return `/services/${serviceId}`;

  return `/services/${serviceId}?OrderType=${encodeURIComponent(orderType)}`;
};

const UNREAD_DOT_STYLE = {
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: '#065fd4',
  flexShrink: 0,
  marginTop: 6,
  marginRight: 10
};

const BELL_BADGE_STYLE = {
  position: 'absolute',
  top: -4,
  right: -8,
  minWidth: 20,
  height: 20,
  padding: '0 4px',
  borderRadius: 10,
  backgroundColor: '#dc3545', // default red
  border: '1.5px solid #fff',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 9,
  fontWeight: 700,
  lineHeight: 1,
  color: '#fff'
};

const NotificationBell = ({ children, notificationsEnabled = true, pendingOrdersCount = 0 }) => {
  const history = useHistory();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(notificationsEnabled);
  const [hoveredId, setHoveredId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [notificationToDelete, setNotificationToDelete] = useState(null);
  const reconnectTimer = useRef(null);
  const disconnectRef = useRef(null);
  const seenNotificationIdsRef = useRef(new Set());
  const audioCtxRef = useRef(null);

  const showDropdown = notificationsEnabled || Boolean(children);

  const playNotificationSound = useCallback(() => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;

      if (!audioCtxRef.current) audioCtxRef.current = new AudioCtx();
      const ctx = audioCtxRef.current;

      // Some browsers start audio contexts in "suspended" until user interacts.
      // Resume if possible; if it fails, just silently skip sound.
      if (ctx.state === 'suspended') {
        ctx.resume?.().catch(() => {});
      }

      // creating beep sound without .mp3 or .wav file
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = 880;
      gain.gain.setValueAtTime(0.001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.18);

      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.start();
      oscillator.stop(ctx.currentTime + 0.2);
    } catch {}
  }, []);

  const fetchInitial = useCallback(async () => {
    if (!notificationsEnabled) {
      setLoading(false);
      return;
    }

    const [{ response: listRes }, { response: countRes }] = await Promise.all([
      makeRESTApiRequest({
        endpoint: `${ENDPOINTS.NOTIFICATIONS}?limit=500`,
        method: 'GET'
      }),
      makeRESTApiRequest({
        endpoint: ENDPOINTS.NOTIFICATIONS_UNREAD_COUNT,
        method: 'GET'
      })
    ]);

    if (listRes?.notifications) {
      setNotifications(listRes.notifications);
      seenNotificationIdsRef.current = new Set(listRes.notifications.map(n => n._id));
    }
    if (typeof countRes?.count === 'number') {
      setUnreadCount(countRes.count);
    }
    setLoading(false);
  }, [notificationsEnabled]);

  const connectStream = useCallback(() => {
    if (!notificationsEnabled) return () => {};

    return connectNotificationStream({
      onEvent: (event, data) => {
        if (event === 'connected' && typeof data?.unreadCount === 'number') {
          setUnreadCount(data.unreadCount);
        }
        if (event === 'unread_count' && typeof data?.count === 'number') {
          setUnreadCount(data.count);
        }
        if (event === 'notification.created' && data?.notification) {
          const n = data.notification;
          const isNew = n?._id && !seenNotificationIdsRef.current.has(n._id);

          if (isNew) {
            seenNotificationIdsRef.current.add(n._id);
            const title = NOTIFICATION_LABELS[n.type] || n.type || 'Notification';
            const msg = getNotificationMessage(n);

            toast(title, {
              description: msg,
              duration: 7000,
              position: 'top-right',
              action: {
                label: 'Open',
                onClick: () => openNotification(n)
              }
            });
            playNotificationSound();
          }

          setNotifications(prev => {
            const exists = prev.some(x => x._id === n._id);
            if (exists) return prev;
            return [n, ...prev];
          });
        }
      },
      onError: () => {
        if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
        reconnectTimer.current = setTimeout(() => {
          disconnectRef.current?.();
          disconnectRef.current = connectStream();
        }, 5000);
      }
    });
  }, [notificationsEnabled, playNotificationSound]);

  useEffect(() => {
    if (!notificationsEnabled) {
      setLoading(false);
      return;
    }

    fetchInitial();
    disconnectRef.current = connectStream();

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchInitial();
        disconnectRef.current?.();
        disconnectRef.current = connectStream();
      } else {
        disconnectRef.current?.();
      }
    };

    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      disconnectRef.current?.();
    };
  }, [connectStream, fetchInitial, notificationsEnabled]);

  const markAllRead = async () => {
    await makeRESTApiRequest({
      endpoint: ENDPOINTS.NOTIFICATIONS_READ_ALL,
      method: 'PATCH'
    });
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const requestDeleteNotification = (notification, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (deletingId) return;
    setNotificationToDelete(notification);
  };

  const confirmDeleteNotification = async () => {
    const notification = notificationToDelete;
    if (!notification || deletingId) return;

    setDeletingId(notification._id);

    const { error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.NOTIFICATION_DELETE(notification._id),
      method: 'DELETE'
    });

    setDeletingId(null);
    setNotificationToDelete(null);

    if (error) return;

    setNotifications(prev => prev.filter(n => n._id !== notification._id));
    if (!notification.read) {
      setUnreadCount(c => Math.max(0, c - 1));
    }
  };

  const openNotification = async notification => {
    if (!notification.read) {
      await makeRESTApiRequest({
        endpoint: ENDPOINTS.NOTIFICATION_READ(notification._id),
        method: 'PATCH'
      });
      setUnreadCount(c => Math.max(0, c - 1));
      setNotifications(prev => prev.map(n => (n._id === notification._id ? { ...n, read: true } : n)));
    }

    const servicePath = getServiceNotificationPath(notification);
    if (servicePath) {
      history.push(servicePath);
      return;
    }

    const taskId = notification.metadata?.task;
    if (taskId) {
      history.push(`/tasks?taskId=${taskId}`);
    } else {
      history.push('/tasks');
    }
  };

  if (!showDropdown) {
    return null;
  }

  return (
    <>
      <AlertModal
        show={Boolean(notificationToDelete)}
        onHide={() => {
          if (!deletingId) setNotificationToDelete(null);
        }}
        alertText="Are you sure you want to remove this notification?"
        dismissButtonText="Cancel"
        continueButtonText="Remove"
        continueButtonVariant="danger"
        onDismissClick={() => {
          if (!deletingId) setNotificationToDelete(null);
        }}
        onContinueClick={confirmDeleteNotification}
        showProgress={Boolean(deletingId && notificationToDelete)}
        progressText="Removing..."
      />
      <Dropdown alignRight drop="auto">
        <Dropdown.Toggle
          variant="light"
          size="sm"
          className="d-flex align-items-center justify-content-center border-0 bg-transparent shadow-none position-relative dropdown-toggle-no-arrow p-1"
          id="notification-bell-dropdown"
          title={notificationsEnabled && unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications'}
          aria-label={
            notificationsEnabled && unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'
          }
        >
          <Bell size={24} className="text-dark" aria-hidden />
          {notificationsEnabled && unreadCount > 0 ? (
            <span style={BELL_BADGE_STYLE} aria-hidden>
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          ) : pendingOrdersCount > 0 ? (
            <span style={{ ...BELL_BADGE_STYLE, backgroundColor: '#f59e0b', color: 'black' }} aria-hidden>
              {pendingOrdersCount > 99 ? '99+' : pendingOrdersCount}
            </span>
          ) : null}
        </Dropdown.Toggle>
        <Dropdown.Menu
          popperConfig={{
            strategy: 'fixed',
            modifiers: {
              preventOverflow: { enabled: true, boundariesElement: 'viewport' },
              flip: { enabled: true }
            }
          }}
          style={{
            width: 340,
            maxHeight: 420,
            overflowY: 'auto',
            zIndex: 1100
          }}
        >
          <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
            <span className="font-weight-bold small">Notifications</span>
            {notificationsEnabled && unreadCount > 0 && (
              <button type="button" className="btn btn-link btn-sm p-0 text-info" onClick={markAllRead}>
                Mark all read
              </button>
            )}
          </div>

          {children && (
            <div className="px-3 py-2 border-bottom" onClick={e => e.stopPropagation()}>
              {children}
            </div>
          )}

          {notificationsEnabled && loading && (
            <Dropdown.ItemText className="text-muted small">Loading…</Dropdown.ItemText>
          )}
          {notificationsEnabled && !loading && notifications.length === 0 && !children && (
            <Dropdown.ItemText className="text-muted small">No notifications</Dropdown.ItemText>
          )}
          {notificationsEnabled && !loading && notifications.length === 0 && children && (
            <Dropdown.ItemText className="text-muted small px-3">No task notifications</Dropdown.ItemText>
          )}
          {notificationsEnabled &&
            notifications.map(n => {
              const isHovered = hoveredId === n._id;
              const msg = getNotificationMessage(n);
              return (
                <Dropdown.Item
                  key={n._id}
                  as="div"
                  className="p-0"
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={() => setHoveredId(n._id)}
                  onMouseLeave={() => setHoveredId(null)}
                >
                  <div
                    className="d-flex align-items-start px-3 py-2 position-relative"
                    style={{
                      backgroundColor: isHovered ? '#f2f2f2' : !n.read ? 'rgba(6, 95, 212, 0.06)' : 'transparent'
                    }}
                  >
                    {!n.read ? (
                      <span style={UNREAD_DOT_STYLE} aria-hidden />
                    ) : (
                      <span style={{ width: 18, flexShrink: 0 }} aria-hidden />
                    )}
                    <div
                      className="flex-grow-1 min-width-0"
                      style={{ whiteSpace: 'normal' }}
                      onClick={() => openNotification(n)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openNotification(n);
                        }
                      }}
                    >
                      <div className="small font-weight-bold text-dark">{NOTIFICATION_LABELS[n.type] || n.type}</div>
                      <div className="small text-muted">{msg}</div>
                      <div className="small text-muted xtinyFont text-right">{moment(n.createdAt).fromNow()}</div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-link p-0 ml-2 flex-shrink-0 text-muted"
                      title="Remove notification"
                      aria-label="Remove notification"
                      style={{
                        opacity: isHovered ? 1 : 0,
                        pointerEvents: isHovered ? 'auto' : 'none',
                        transition: 'opacity 0.15s ease',
                        lineHeight: 1,
                        marginTop: 2
                      }}
                      disabled={deletingId === n._id}
                      onClick={e => requestDeleteNotification(n, e)}
                    >
                      <X size={16} className="text-danger" />
                    </button>
                  </div>
                </Dropdown.Item>
              );
            })}
        </Dropdown.Menu>
      </Dropdown>
    </>
  );
};

export default NotificationBell;
